import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Trash2, Loader2, Ban, CalendarPlus, Check, Clock, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { AppointmentEditor } from "./AppointmentEditor";

const toMin = (hhmm) => parseInt(hhmm.split(":")[0], 10) * 60 + parseInt(hhmm.split(":")[1], 10);
const toHHMM = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

function computeFreeSlots(day, todayStr, nowMinutes) {
  if (!day || !day.open || !day.start || day.blocked_day) return [];
  const taken = day.appointments.map((a) => [toMin(a.time), toMin(a.time) + (a.duration || 30)]);
  const blocked = day.blocks.filter((b) => b.time).map((b) => [toMin(b.time), toMin(b.time) + 30]);
  const isToday = day.date === todayStr;
  const slots = [];
  for (let tm = toMin(day.start); tm + 30 <= toMin(day.end); tm += 30) {
    if (isToday && tm < nowMinutes) continue;
    const overlaps = [...taken, ...blocked].some(([s, e]) => tm < e && s < tm + 30);
    if (!overlaps) slots.push(toHHMM(tm));
  }
  return slots;
}

const DAYS_FR = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

function dayState(d) {
  if (!d.open || d.capacity === 0) return "closed";
  if (d.blocked_day) return "blocked";
  if (d.booked_units === 0) return "empty";
  if (d.booked_units >= d.capacity) return "full";
  return "partial";
}

const CELL_STYLE = {
  empty: "bg-[#4A6B53] border-[#4A6B53] text-cream hover:brightness-110",
  partial: "bg-[#C98A2D] border-[#C98A2D] text-cream hover:brightness-110",
  full: "bg-[#9E4747] border-[#9E4747] text-cream hover:brightness-110",
  closed: "bg-beige/40 border-taupe/15 text-espresso/30",
  blocked: "bg-espresso/70 border-espresso/70 text-cream/70 hover:bg-espresso/80",
};

const LABEL_STYLE = {
  empty: "text-cream/85",
  partial: "text-cream/90",
  full: "text-cream/90",
  closed: "text-espresso/30",
  blocked: "text-cream/60",
};

const STATUS_FR = { pending: "En attente", confirmed: "Confirmé" };

export const Planning = () => {
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [days, setDays] = useState(null);
  const [selected, setSelected] = useState(null);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [rdv, setRdv] = useState({ service_id: "", time: "", name: "", phone: "", message: "" });
  const [slots, setSlots] = useState([]);
  const [blockTime, setBlockTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const monthStr = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}`;
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  const load = () =>
    api.get("/admin/calendar", { params: { month: monthStr } })
      .then((r) => setDays(r.data.days))
      .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => { setSelected(null); load(); }, [monthStr]);
  useEffect(() => {
    api.get("/admin/services").then((r) => setServices(r.data.services.filter((s) => s.active))).catch(() => {});
    api.get("/admin/clients").then((r) => setClients(r.data.clients)).catch(() => {});
  }, []);

  const nameSuggestions = rdv.name.trim().length >= 1
    ? clients.filter((c) => c.name.toLowerCase().includes(rdv.name.trim().toLowerCase()) && c.name.toLowerCase() !== rdv.name.trim().toLowerCase()).slice(0, 4)
    : [];

  useEffect(() => {
    if (!rdv.service_id || !selected) { setSlots([]); return; }
    api.get("/availability", { params: { service_id: rdv.service_id, days: 40 } })
      .then((r) => {
        const day = r.data.days.find((d) => d.date === selected);
        setSlots(day ? day.times : []);
      })
      .catch(() => setSlots([]));
  }, [rdv.service_id, selected, days]);

  const day = days?.find((d) => d.date === selected);

  const move = (delta) => {
    const m = cursor.m + delta;
    setCursor({ y: cursor.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 });
  };

  const addBlock = async (time) => {
    setBusy(true);
    try {
      await api.post("/admin/blocks", { date: selected, time: time || null, note: "" });
      toast.success(time ? `Créneau ${time} bloqué` : "Journée bloquée");
      setBlockTime("");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const removeBlock = async (id) => {
    await api.delete(`/admin/blocks/${id}`);
    toast.success("Blocage supprimé");
    load();
  };

  const createRdv = async () => {
    setBusy(true);
    try {
      await api.post("/admin/appointments", { ...rdv, date: selected });
      toast.success("Rendez-vous confirmé et ajouté");
      setRdv({ service_id: "", time: "", name: "", phone: "", message: "" });
      load();
    } catch (e) { toast.error(formatApiError(e, "Créneau indisponible")); } finally { setBusy(false); }
  };

  const offset = (new Date(cursor.y, cursor.m, 1).getDay() + 6) % 7;
  const inputCls =
    "bg-white border border-taupe/30 rounded-md px-3.5 py-2.5 text-sm text-espresso placeholder:text-espresso/40 focus:outline-none focus:ring-1 focus:ring-taupe transition-shadow duration-300 w-full";

  return (
    <div data-testid="admin-planning-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button data-testid="cal-prev" onClick={() => move(-1)} className="w-9 h-9 rounded-full border border-taupe/30 flex items-center justify-center text-espresso/60 hover:border-taupe hover:text-espresso transition-colors duration-300">
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <h3 className="font-display text-2xl text-espresso capitalize w-44 text-center" data-testid="cal-month-label">
            {MONTHS_FR[cursor.m]} {cursor.y}
          </h3>
          <button data-testid="cal-next" onClick={() => move(1)} className="w-9 h-9 rounded-full border border-taupe/30 flex items-center justify-center text-espresso/60 hover:border-taupe hover:text-espresso transition-colors duration-300">
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex flex-wrap gap-4 text-xs text-espresso/60" data-testid="cal-legend">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#4A6B53]" /> Disponible</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#C98A2D]" /> Se remplit</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#9E4747]" /> Complet</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-espresso/60" /> Fermé / Bloqué</span>
        </div>
      </div>

      {!days ? (
        <div className="flex items-center gap-3 text-sm text-espresso/55 py-10">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : (
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 bg-white border border-taupe/20 rounded-2xl p-4 lg:p-6" data-testid="planning-calendar">
            <div className="grid grid-cols-7 gap-1.5 mb-2">
              {DAYS_FR.map((d) => (
                <div key={d} className="text-center text-[10px] tracking-[0.12em] uppercase text-espresso/45 py-1">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {Array.from({ length: offset }).map((_, i) => <div key={`b${i}`} />)}
              {days.map((d) => {
                const st = dayState(d);
                const isSelected = selected === d.date;
                const isToday = d.date === todayStr;
                return (
                  <button
                    key={d.date}
                    data-testid={`day-cell-${d.date}`}
                    onClick={() => setSelected(d.date)}
                    className={`relative rounded-xl border px-1.5 py-2 text-left transition-[background-color,border-color,box-shadow] duration-300 min-h-[64px] ${CELL_STYLE[st]} ${isSelected ? "ring-2 ring-espresso" : ""}`}
                  >
                    <span className={`text-sm font-medium ${isToday ? "inline-flex w-6 h-6 rounded-full bg-cream text-espresso items-center justify-center" : ""}`}>
                      {parseInt(d.date.slice(8), 10)}
                    </span>
                    <span className={`block text-[10px] mt-0.5 leading-tight ${st === "closed" ? LABEL_STYLE.closed : LABEL_STYLE[st]}`}>
                      {st === "empty" && "Libre"}
                      {st === "partial" && `${d.appointments.length} RDV`}
                      {st === "full" && "Complet"}
                      {st === "closed" && "Fermé"}
                      {st === "blocked" && "Bloqué"}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-5" data-testid="day-panel">
            {!day ? (
              <div className="bg-white border border-taupe/20 rounded-2xl p-8 text-center text-sm text-espresso/45">
                Cliquez sur un jour pour voir le détail, ajouter un rendez-vous ou bloquer.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-white border border-taupe/20 rounded-2xl p-5">
                  <h4 className="font-display text-xl text-espresso capitalize" data-testid="day-panel-title">
                    {day.weekday} {parseInt(day.date.slice(8), 10)} {MONTHS_FR[parseInt(day.date.slice(5, 7), 10) - 1]}
                  </h4>
                  <div className="mt-3 space-y-2">
                    {day.appointments.length === 0 && (
                      <p className="text-sm text-espresso/45" data-testid="day-no-rdv">Aucun rendez-vous.</p>
                    )}
                    {day.appointments.map((a) => (
                      <div key={a.id}>
                        <div className="flex items-center justify-between gap-3 border border-taupe/15 rounded-xl px-4 py-2.5" data-testid={`day-rdv-${a.id}`}>
                          <div>
                            <p className="text-sm text-espresso font-medium">{a.time} — {a.name}</p>
                            <p className="text-xs text-espresso/50">{a.service_name} · {a.duration} min</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {a.presence_confirmed && <Check className="w-3.5 h-3.5 text-[#4A6B53]" />}
                            <span className={`text-[10px] tracking-[0.08em] uppercase px-2.5 py-1 rounded-full ${a.status === "confirmed" ? "bg-[#4A6B53]/10 text-[#4A6B53]" : "bg-gold/15 text-taupe-dark"}`}>
                              {STATUS_FR[a.status]}
                            </span>
                            <button
                              data-testid={`day-edit-${a.id}`}
                              onClick={() => setEditingId(editingId === a.id ? null : a.id)}
                              title="Modifier"
                              className="text-espresso/40 hover:text-espresso transition-colors duration-300"
                            >
                              <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                            </button>
                          </div>
                        </div>
                        {editingId === a.id && (
                          <div className="mt-2">
                            <AppointmentEditor
                              appt={a}
                              services={services}
                              onClose={() => setEditingId(null)}
                              onSaved={() => { setEditingId(null); load(); }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                    {day.blocks.filter((b) => b.time).map((b) => (
                      <div key={b.id} className="flex items-center justify-between gap-3 border border-espresso/20 bg-espresso/5 rounded-xl px-4 py-2.5" data-testid={`day-block-${b.id}`}>
                        <p className="text-sm text-espresso/60"><Ban className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" strokeWidth={1.5} />{b.time} bloqué</p>
                        <button data-testid={`day-block-del-${b.id}`} onClick={() => removeBlock(b.id)} className="text-espresso/40 hover:text-[#9E4747] transition-colors duration-300">
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                    ))}
                  </div>

                  {day.open && !day.blocked_day && (
                    <div className="mt-4 pt-4 border-t border-taupe/15">
                      <p className="text-[10px] tracking-[0.16em] uppercase text-espresso/50 mb-2.5 flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> Créneaux libres
                      </p>
                      {(() => {
                        const free = computeFreeSlots(day, todayStr, nowMinutes);
                        if (free.length === 0) return <p className="text-xs text-espresso/45" data-testid="day-no-free-slots">Aucun créneau libre.</p>;
                        return (
                          <div className="flex flex-wrap gap-1.5" data-testid="day-free-slots">
                            {free.map((slot) => (
                              <span
                                key={slot}
                                className={`inline-flex items-center rounded-full border text-xs transition-colors duration-300 ${
                                  rdv.time === slot ? "border-espresso bg-espresso text-cream" : "border-[#4A6B53]/40 bg-[#4A6B53]/8 text-[#3d5845]"
                                }`}
                              >
                                <button
                                  data-testid={`free-slot-${slot}`}
                                  onClick={() => setRdv({ ...rdv, time: slot })}
                                  title="Utiliser ce créneau pour le rendez-vous ci-dessous"
                                  className="px-3 py-1.5"
                                >
                                  {slot}
                                </button>
                                <button
                                  data-testid={`free-slot-block-${slot}`}
                                  onClick={() => addBlock(slot)}
                                  title="Bloquer ce créneau"
                                  className={`pr-2.5 pl-1 py-1.5 transition-colors duration-300 ${rdv.time === slot ? "text-cream/70 hover:text-cream" : "text-[#3d5845]/50 hover:text-[#9E4747]"}`}
                                >
                                  <Ban className="w-3 h-3" strokeWidth={1.5} />
                                </button>
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-taupe/15 flex flex-wrap gap-2">
                    <input type="time" className={`${inputCls} !w-auto`} data-testid="day-block-time" value={blockTime} onChange={(e) => setBlockTime(e.target.value)} />
                    <button data-testid="day-block-slot-btn" disabled={!blockTime || busy} onClick={() => addBlock(blockTime)} className="rounded-full border border-espresso/30 text-espresso/70 px-4 py-2 text-xs tracking-[0.08em] uppercase hover:border-espresso transition-colors duration-300 disabled:opacity-40">
                      Bloquer l'heure
                    </button>
                    {day.blocked_day ? (
                      <button data-testid="day-unblock-btn" disabled={busy} onClick={() => removeBlock(day.blocks.find((b) => !b.time)?.id)} className="rounded-full bg-espresso text-cream px-4 py-2 text-xs tracking-[0.08em] uppercase hover:bg-taupe transition-colors duration-300 disabled:opacity-40">
                        Débloquer la journée
                      </button>
                    ) : (
                      <button data-testid="day-block-all-btn" disabled={busy} onClick={() => addBlock(null)} className="rounded-full bg-espresso text-cream px-4 py-2 text-xs tracking-[0.08em] uppercase hover:bg-taupe transition-colors duration-300 disabled:opacity-40">
                        Bloquer la journée
                      </button>
                    )}
                  </div>
                </div>

                <div className="bg-white border border-taupe/20 rounded-2xl p-5">
                  <h4 className="font-display text-xl text-espresso flex items-center gap-2">
                    <CalendarPlus className="w-4 h-4 text-taupe" strokeWidth={1.5} /> Ajouter un rendez-vous
                  </h4>
                  <div className="mt-4 space-y-3">
                    <select className={inputCls} data-testid="rdv-service-select" value={rdv.service_id} onChange={(e) => setRdv({ ...rdv, service_id: e.target.value, time: "" })}>
                      <option value="">Choisir la prestation…</option>
                      {services.map((s) => <option key={s.id} value={s.id}>{s.name}{s.admin_only ? " (réservée admin)" : ""} — {s.price_label}</option>)}
                    </select>
                    {rdv.service_id && (
                      <select className={inputCls} data-testid="rdv-time-select" value={rdv.time} onChange={(e) => setRdv({ ...rdv, time: e.target.value })}>
                        <option value="">Choisir l'horaire…</option>
                        {slots.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    )}
                    {rdv.service_id && slots.length === 0 && (
                      <p className="text-xs text-[#9E4747]" data-testid="rdv-no-slots">Aucun créneau libre ce jour pour cette prestation.</p>
                    )}
                    <div className="relative">
                      <input className={inputCls} data-testid="rdv-name-input" placeholder="Nom de la cliente" value={rdv.name} onChange={(e) => setRdv({ ...rdv, name: e.target.value })} />
                      {nameSuggestions.length > 0 && (
                        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-taupe/30 rounded-xl shadow-[0_12px_30px_rgba(44,36,27,0.12)] overflow-hidden z-20" data-testid="rdv-name-suggestions">
                          {nameSuggestions.map((c, i) => (
                            <button
                              key={i}
                              data-testid={`rdv-name-suggestion-${i}`}
                              onClick={() => setRdv({ ...rdv, name: c.name, phone: c.phone })}
                              className="w-full text-left px-4 py-2.5 text-sm hover:bg-cream transition-colors duration-200 flex items-center justify-between gap-3"
                            >
                              <span className="text-espresso">{c.name}</span>
                              <span className="text-xs text-espresso/45">{c.phone} · {c.visits} visite{c.visits > 1 ? "s" : ""}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <input className={inputCls} data-testid="rdv-phone-input" placeholder="Téléphone" value={rdv.phone} onChange={(e) => setRdv({ ...rdv, phone: e.target.value })} />
                    <button
                      data-testid="rdv-create-btn"
                      disabled={!rdv.service_id || !rdv.time || !rdv.name.trim() || !rdv.phone.trim() || busy}
                      onClick={createRdv}
                      className="w-full rounded-full bg-taupe text-cream px-6 py-3.5 text-sm tracking-[0.12em] uppercase hover:bg-taupe-dark transition-colors duration-300 disabled:opacity-40 inline-flex items-center justify-center gap-2"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={1.5} />}
                      Confirmer le rendez-vous
                    </button>
                    <p className="text-xs text-espresso/45">Le rendez-vous est directement confirmé et bloque le créneau sur le site.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
